/**
 * Yap's first-run tour definition (docs/product/onboarding-tour/yap-tour-plan.md).
 *
 * Steps are rebuilt on every render so a language switch mid-tour re-renders
 * with fresh strings. Mode-scoped steps (sketch toolbar) only anchor in their
 * own workbench and otherwise degrade to centered cards — the tour never
 * navigates between /bim, /2d, /3d and /sketch on the user's behalf (plan §3).
 */
import { useTranslation } from 'react-i18next';
import { useFirstRunTour, type TourConfig, type TourLabels } from '@suite/tour';

export function useYapTour(): TourConfig {
  const { t } = useTranslation();
  return {
    appId: 'yap',
    version: 1,
    steps: [
      { id: 'welcome', target: '', title: t('tour.welcome.title'), body: t('tour.welcome.body'), placement: 'center' },
      { id: 'mode-cards', target: '[data-tour-id="mode-cards"]', title: t('tour.mode-cards.title'), body: t('tour.mode-cards.body'), placement: 'center' },
      { id: 'sketch-toolbar', target: '[data-tour-id="sketch-toolbar"]', title: t('tour.sketch-toolbar.title'), body: t('tour.sketch-toolbar.body'), placement: 'bottom' },
      { id: 'viewport', target: '[data-tour-id="viewport"]', title: t('tour.viewport.title'), body: t('tour.viewport.body'), placement: 'center' },
      { id: 'task-panels', target: '[data-tour-id="task-panels"]', title: t('tour.task-panels.title'), body: t('tour.task-panels.body'), placement: 'right' },
      { id: 'done', target: '', title: t('tour.done.title'), body: t('tour.done.body'), placement: 'center' },
    ],
  };
}

export function useYapTourLabels(): TourLabels {
  const { t } = useTranslation();
  return {
    next: t('tour.next'),
    back: t('tour.back'),
    skip: t('tour.skip'),
    done: t('tour.finish'),
    start: t('tour.start'),
    stepOf: (current, total) => t('tour.stepOf', { current, total }),
  };
}

/** Auto-start gate, mounted inside the app tree (provider wraps the app from main.tsx). */
export function FirstRunTourGate() {
  const yapTour = useYapTour();
  const labels = useYapTourLabels();
  useFirstRunTour(yapTour, { enabled: true, labels });
  return null;
}
